import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import ChunkErrorBoundary from "@/components/ChunkErrorBoundary";
import AppActivityTracker from "@/components/AppActivityTracker";
import ClientAuthWrapper from "@/components/ClientAuthWrapper";
import { PMClientAuthWrapper } from "@/hooks/usePMClientAuth";
import { ServerStatusBanner } from "@/components/ServerStatusBanner";
import {
  adminAilixPath,
  adminPMClientsPath,
  adminPMPath,
  adminRHPath,
} from "@/lib/adminRoutes";
import Home from "./pages/Home";
import NotFound from "./pages/NotFound";

const Synthese = lazy(() => import("./pages/Synthese"));
const OffreSpeciale = lazy(() => import("./pages/OffreSpeciale"));
const MissionsConfigurateur = lazy(() => import("./pages/MissionsConfigurateur"));
const Cabinet = lazy(() => import("./pages/Cabinet"));
// Redirection /devis → /finalisation (historique devis intégré en drawer)
const DevisRedirect = () => {
  const { clientId } = useParams();
  return <Navigate to={`/${clientId}/finalisation`} replace />;
};
const Finalisation = lazy(() => import("./pages/Finalisation"));
const Pilotage = lazy(() => import("./pages/Pilotage"));
const PilotageProjetDetail = lazy(() => import("./pages/PilotageProjetDetail"));

const AdminList = lazy(() => import("@/pages/admin/AdminList"));
const AdminClient = lazy(() => import("@/pages/admin/AdminClient"));
const AdminAilix = lazy(() => import("@/pages/admin/AdminAilix"));
const ReferrerPortal = lazy(() => import("@/pages/referrer/ReferrerPortal"));
const ReferrerLogin = lazy(() => import("@/pages/referrer/ReferrerLogin"));
const ReferrerSignup = lazy(() => import("@/pages/referrer/ReferrerSignup"));
const SignedDealsKanban = lazy(() => import("@/pages/admin/SignedDealsKanban"));
const AdminClients = lazy(() => import("@/pages/admin/AdminClients"));
const AdminOutils = lazy(() => import("@/pages/admin/AdminOutils"));
const AdminSimulateurRemuneration = lazy(() => import("@/pages/admin/AdminSimulateurRemuneration"));
const PromptGeneratorPage = lazy(() => import("@/pages/admin/PromptGeneratorPage"));
const FactCheckerPage = lazy(() => import("@/pages/admin/FactCheckerPage"));
const InternalAssetsPage = lazy(() => import("@/pages/admin/InternalAssetsPage"));
const AdminRH = lazy(() => import("@/pages/admin/AdminRH"));
const AdminRights = lazy(() => import("@/pages/admin/AdminRights"));
const CreateOrganizationPage = lazy(() => import("@/pages/admin/CreateOrganizationPage"));
const CreateProjectPage = lazy(() => import("@/pages/admin/CreateProjectPage"));
const MissionVersionKanban = lazy(() => import("@/pages/admin/MissionVersionKanban"));
const AdminAuditeur = lazy(() => import("@/pages/admin/AdminAuditeur"));
const AdminAuditeurList = lazy(() => import("@/pages/admin/AdminAuditeurList"));
const AdminCdcMaker = lazy(() => import("@/pages/admin/AdminCdcMaker"));
const AdminCdcMakerList = lazy(() => import("@/pages/admin/AdminCdcMakerList"));
const AdminCdcMakerKbList = lazy(() => import("@/pages/admin/AdminCdcMakerKbList"));
const AdminCdcMakerWorkflow = lazy(() => import("@/pages/admin/AdminCdcMakerWorkflow"));
const AdminPM = lazy(() => import("@/pages/admin/AdminPM"));
const AdminTranscription = lazy(() => import("@/pages/admin/AdminTranscription"));
const TranscriptionDetail = lazy(() => import("@/pages/admin/TranscriptionDetail"));
const ScopeVisualizerPage = lazy(() => import("@/pages/ScopeVisualizerPage"));
const ScopeVisualizerV2Page = lazy(() => import("@/pages/ScopeVisualizerV2Page"));
const ScopeVisualizerClientPage = lazy(() => import("@/pages/ScopeVisualizerClientPage"));
const ScopeClientV2Page = lazy(() => import("@/pages/ScopeClientV2Page"));
const PMClientView = lazy(() => import("@/pages/admin/PMClientView"));
const PMClientManagement = lazy(() => import("@/pages/admin/PMClientManagement"));
const PMClientLogin = lazy(() => import("@/pages/pm/PMClientLogin"));
const PMClientDashboard = lazy(() => import("@/pages/pm/PMClientDashboard"));
const PMClientBoardView = lazy(() => import("@/pages/pm/PMClientBoardView"));
const PMGuestView = lazy(() => import("@/pages/pm/PMGuestView"));

const AiRoadmapParticipant = lazy(() => import("@/pages/AiRoadmapParticipant"));
const AiRoadmapAdmin = lazy(() => import("@/pages/admin/AiRoadmapAdmin"));
const AiRoadmapLiveBoard = lazy(() => import("@/pages/admin/AiRoadmapLiveBoard"));

const ModelWatchPublic = lazy(() => import("@/pages/public/ModelWatchPublic"));
const AdminModelWatch = lazy(() => import("@/pages/admin/AdminModelWatch"));
const AdminBenchmarking = lazy(() => import("@/pages/admin/AdminBenchmarking"));

const AdminTransfer = lazy(() => import("@/pages/admin/AdminTransfer"));
const TransferDownloadPage = lazy(() => import("@/pages/transfer/TransferDownloadPage"));
const TransferDropPage = lazy(() => import("@/pages/transfer/TransferDropPage"));

const PromptLabParticipant = lazy(() => import("@/pages/PromptLabParticipant"));
const PromptLabAdmin = lazy(() => import("@/pages/admin/PromptLabAdmin"));
const PromptLabLiveBoard = lazy(() => import("@/pages/admin/PromptLabLiveBoard"));

const MonitoringShell = lazy(() => import("@/pages/admin/monitoring/MonitoringShell"));
const MonitoringNotifications = lazy(() => import("@/pages/admin/monitoring/MonitoringNotifications"));
const MonitoringRoutes = lazy(() => import("@/pages/admin/monitoring/MonitoringRoutes"));
const MonitoringReliability = lazy(() => import("@/pages/admin/monitoring/views/ReliabilityView"));
const MonitoringTimeline = lazy(() => import("@/pages/admin/monitoring/views/TimelineView"));
const MonitoringProjectDetail = lazy(() => import("@/pages/admin/monitoring/MonitoringProjectDetail"));
const MonitoringCosts = lazy(() => import("@/pages/admin/monitoring/MonitoringCosts"));
const MonitoringActionsLog = lazy(() => import("@/pages/admin/monitoring/MonitoringActionsLog"));

const RequestAnalyticsLayout = lazy(
  () => import("@/pages/admin/request-analytics/RequestAnalyticsLayout")
);
const RequestAnalyticsDashboard = lazy(
  () => import("@/pages/admin/request-analytics/RequestAnalyticsDashboard")
);
const RequestAnalyticsUsers = lazy(
  () => import("@/pages/admin/request-analytics/RequestAnalyticsUsers")
);
const RequestAnalyticsUserDetails = lazy(
  () => import("@/pages/admin/request-analytics/RequestAnalyticsUserDetails")
);
const RequestAnalyticsCosts = lazy(
  () => import("@/pages/admin/request-analytics/RequestAnalyticsCosts")
);
const RequestAnalyticsLogs = lazy(
  () => import("@/pages/admin/request-analytics/RequestAnalyticsLogs")
);
const RequestAnalyticsSettings = lazy(
  () => import("@/pages/admin/request-analytics/RequestAnalyticsSettings")
);
const RequestAnalyticsProjects = lazy(
  () => import("@/pages/admin/request-analytics/RequestAnalyticsProjects")
);
const RequestAnalyticsRecentActivity = lazy(
  () => import("@/pages/admin/request-analytics/RequestAnalyticsRecentActivity")
);
const RequestAnalyticsCodebase = lazy(
  () => import("@/pages/admin/request-analytics/RequestAnalyticsCodebase")
);
const RequestAnalyticsFeatures = lazy(
  () => import("@/pages/admin/request-analytics/RequestAnalyticsFeatures")
);

const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[400px]">
    <div className="flex flex-col items-center gap-3">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      <span className="text-sm text-muted-foreground">Chargement...</span>
    </div>
  </div>
);

// Configuration de React Query pour désactiver les retries par défaut
// Le système de retry est géré par apiFetch dans /lib/api.ts
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false, // Désactiver le retry automatique de React Query
      refetchOnWindowFocus: false, // Éviter les refetch inutiles
    },
    mutations: {
      retry: false, // Les mutations ne doivent jamais être retried automatiquement
    },
  },
});

const App = () => (
  <ChunkErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <ServerStatusBanner />
        <BrowserRouter>
          <AppActivityTracker />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/synthese" element={<Synthese />} />
              <Route path="/missions" element={<MissionsConfigurateur />} />
              <Route path="/cabinet" element={<Cabinet />} />
              <Route path="/finalisation" element={<Finalisation />} />
              {/* Admin */}
              <Route path="/admin" element={<AdminList />} />
              <Route path="/admin/ailix/signed-kanban" element={<SignedDealsKanban />} />
              <Route path="/admin/ailix" element={<Navigate to={adminAilixPath()} replace />} />
              <Route path="/admin/ailix/*" element={<AdminAilix />} />
              <Route path="/apporteur" element={<ReferrerPortal />} />
              <Route path="/apporteur/connexion" element={<ReferrerLogin />} />
              <Route path="/apporteur/inscription" element={<ReferrerSignup />} />
              <Route path="/admin/clients" element={<AdminClients />} />
              <Route path="/admin/outils" element={<AdminOutils />} />
              <Route path="/admin/simulateur-remuneration" element={<AdminSimulateurRemuneration />} />
              <Route path="/admin/outils/prompt-generator" element={<PromptGeneratorPage />} />
              <Route path="/admin/outils/prompt-generator/:historyId" element={<PromptGeneratorPage />} />
              <Route path="/admin/outils/fact-checker" element={<FactCheckerPage />} />
              <Route path="/admin/outils/internal-assets" element={<InternalAssetsPage />} />
              <Route path="/admin/rh" element={<Navigate to={adminRHPath()} replace />} />
              <Route path="/admin/rh/*" element={<AdminRH />} />
              <Route path="/admin/rights" element={<AdminRights />} />
              <Route path="/admin/rights/*" element={<AdminRights />} />
              <Route path="/admin/auditeur" element={<AdminAuditeurList />} />
              <Route path="/admin/pm/people" element={<Navigate to={adminRHPath("salaries")} replace />} />
              <Route path="/admin/pm/conges" element={<Navigate to={adminRHPath("planning")} replace />} />
              <Route path="/admin/pm" element={<Navigate to={adminPMPath()} replace />} />
              <Route path="/admin/pm/kanban" element={<AdminPM />} />
              <Route path="/admin/pm/*" element={<AdminPM />} />
              <Route path="/admin/cdc-maker" element={<AdminCdcMakerList />} />
              {/* Ancienne URL autonome, livrée puis déplacée sous « Gestion de projet ». */}
              <Route path="/admin/projets" element={<Navigate to={adminPMPath("projets")} replace />} />
              <Route path="/admin/cdc-maker/kb" element={<AdminCdcMakerKbList />} />
              <Route path="/admin/transcription" element={<AdminTranscription />} />
              <Route path="/admin/transcription/:id" element={<TranscriptionDetail />} />
              <Route path="/admin/analyse-requetes" element={<RequestAnalyticsLayout />}>
                <Route index element={<RequestAnalyticsDashboard />} />
                <Route path="recent" element={<RequestAnalyticsRecentActivity />} />
                <Route path="projects" element={<RequestAnalyticsProjects />} />
                <Route path="users" element={<RequestAnalyticsUsers />} />
                <Route path="users/:email" element={<RequestAnalyticsUserDetails />} />
                <Route path="costs" element={<RequestAnalyticsCosts />} />
                <Route path="logs" element={<RequestAnalyticsLogs />} />
                <Route path="codebase" element={<RequestAnalyticsCodebase />} />
                <Route path="settings" element={<RequestAnalyticsSettings />} />
                <Route path="features" element={<RequestAnalyticsFeatures />} />
              </Route>
              <Route path="/admin/create-organization" element={<CreateOrganizationPage />} />
              <Route path="/admin/:orgId" element={<AdminClient />} />
              <Route path="/admin/:orgId/create-project" element={<CreateProjectPage />} />
              <Route path="/admin/:orgId/auditeur" element={<AdminAuditeur />} />
              <Route path="/admin/:orgId/cdc-maker" element={<AdminCdcMaker />} />
              <Route path="/admin/:orgId/cdc-maker/kb" element={<AdminCdcMakerWorkflow />} />
              <Route path="/admin/:orgId/kanban" element={<MissionVersionKanban />} />
              <Route path="/admin/scope-visualizer" element={<ScopeVisualizerPage />} />
              <Route path="/admin/scope-v2" element={<ScopeVisualizerV2Page />} />
              <Route path="/scope/:token" element={<ScopeVisualizerClientPage />} />
              <Route path="/scope-v2/:token" element={<ScopeClientV2Page />} />
              {/* PM Admin - Client management */}
              <Route path="/admin/pm/clients" element={<Navigate to={adminPMClientsPath()} replace />} />
              <Route path="/admin/pm/clients/*" element={<PMClientManagement />} />
              {/* PM Client View - Public page for legacy token-based access */}
              <Route path="/pm/shared/:token" element={<PMClientView />} />
              {/* PM Client Auth - Login and dashboard for authenticated clients */}
              <Route path="/pm/login" element={<PMClientLogin />} />
              <Route path="/pm/auth/callback" element={<PMClientLogin />} />
              <Route
                path="/pm/dashboard"
                element={
                  <PMClientAuthWrapper>
                    <PMClientDashboard />
                  </PMClientAuthWrapper>
                }
              />
              <Route
                path="/pm/board/:boardId"
                element={
                  <PMClientAuthWrapper>
                    <PMClientBoardView />
                  </PMClientAuthWrapper>
                }
              />
              {/* PM Guest View - Public page for guest access via token */}
              <Route path="/pm/guest/:token" element={<PMGuestView />} />
              {/* AI Roadmap Express */}
              <Route path="/fdr" element={<AiRoadmapParticipant />} />
              <Route path="/feuille-de-route-ia" element={<AiRoadmapParticipant />} />
              <Route path="/admin/feuille-de-route-ia" element={<AiRoadmapAdmin />} />
              <Route path="/admin/feuille-de-route-ia/:sessionId" element={<AiRoadmapLiveBoard />} />
              {/* Prompt Lab */}
              <Route path="/prompt-lab" element={<PromptLabParticipant />} />
              <Route path="/admin/prompt-lab" element={<PromptLabAdmin />} />
              <Route path="/admin/prompt-lab/:sessionId" element={<PromptLabLiveBoard />} />
              <Route path="/admin/monitoring" element={<MonitoringShell />} />
              {/* Routes statiques AVANT `:projectId` pour éviter qu'elles ne soient captées comme un id. */}
              <Route path="/admin/monitoring/notifications" element={<MonitoringNotifications />} />
              <Route path="/admin/monitoring/routes" element={<MonitoringRoutes />} />
              <Route path="/admin/monitoring/reliability" element={<MonitoringReliability />} />
              <Route path="/admin/monitoring/timeline" element={<MonitoringTimeline />} />
              <Route path="/admin/monitoring/costs" element={<MonitoringCosts />} />
              <Route path="/admin/monitoring/actions-log" element={<MonitoringActionsLog />} />
              <Route path="/admin/monitoring/:projectId" element={<MonitoringProjectDetail />} />
              {/* Veille Modèles IA — page admin réservée (access_model_watch). */}
              <Route path="/admin/veille-modeles" element={<AdminModelWatch />} />
              <Route path="/admin/benchmarking/*" element={<AdminBenchmarking />} />
              {/* Page publique « Veille Modèles IA » (sans auth, crawlable).
                  DOIT rester AVANT la route attrape-tout :clientId ci-dessous,
                  sinon "veille-modeles" serait capté comme un clientId. */}
              <Route path="/veille-modeles" element={<ModelWatchPublic />} />
              {/* Ailix Transfer — module admin réservé (access_transfer). */}
              <Route path="/admin/transfert" element={<AdminTransfer />} />
              {/* Pages publiques de transfert (sans auth, token en path).
                  DOIVENT rester AVANT la route attrape-tout :clientId ci-dessous
                  (sinon "t" serait capté comme un clientId). Ordre : /t/d/:token (dépôt)
                  avant /t/:token (téléchargement). */}
              <Route path="/t/d/:token" element={<TransferDropPage />} />
              <Route path="/t/:token" element={<TransferDownloadPage />} />
              {/* Routes multi-client paramétrées avec authentification */}
              <Route
                path=":clientId"
                element={
                  <ClientAuthWrapper>
                    <Home />
                  </ClientAuthWrapper>
                }
              />
              <Route
                path=":clientId/synthese"
                element={
                  <ClientAuthWrapper>
                    <Synthese />
                  </ClientAuthWrapper>
                }
              />
              <Route
                path=":clientId/missions"
                element={
                  <ClientAuthWrapper>
                    <MissionsConfigurateur />
                  </ClientAuthWrapper>
                }
              />
              <Route
                path=":clientId/devis"
                element={<DevisRedirect />}
              />
              <Route
                path=":clientId/cabinet"
                element={
                  <ClientAuthWrapper>
                    <Cabinet />
                  </ClientAuthWrapper>
                }
              />
              <Route
                path=":clientId/offre-speciale"
                element={
                  <ClientAuthWrapper>
                    <OffreSpeciale />
                  </ClientAuthWrapper>
                }
              />
              <Route
                path=":clientId/finalisation"
                element={
                  <ClientAuthWrapper>
                    <Finalisation />
                  </ClientAuthWrapper>
                }
              />
              <Route
                path=":clientId/pilotage"
                element={
                  <ClientAuthWrapper>
                    <Pilotage />
                  </ClientAuthWrapper>
                }
              />
              <Route
                path=":clientId/pilotage/projet/:itemId"
                element={
                  <ClientAuthWrapper>
                    <PilotageProjetDetail />
                  </ClientAuthWrapper>
                }
              />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ChunkErrorBoundary>
);

export default App;
